/**
 * Event verification config — derived from the backend
 * `Course.eventVerification` field.
 *
 * Per-event admin configuration controlling how the LMS records
 * attendance for external events (webinars, live courses, conferences).
 * The persisted shape (`CourseEventVerification` from the API client) has
 * every field optional; this module normalizes it into a fully-populated
 * `EventVerificationConfig`. Writes go through `coursesClient.updateCourse`
 * with a `settings.eventVerification` payload — see `utils/courseSettings.ts`.
 *
 * SECURITY: `codeMatches` compares the learner-entered code against the code
 * carried in `course.eventVerification.code`. This is a convenience/UX gate
 * only — the backend MUST re-validate the code (and all credit issuance) on
 * `completeExternalCourse`; never trust the client-side match alone.
 *
 * Verification methods (all OR-combined when multiple are enabled):
 *
 *   • Confirmation code — admin enters a string per event, reveals it
 *     live (slide, chat, verbal). Learner enters it on the event page
 *     to mark complete. Lightweight; works for live + virtual.
 *
 *   • Post-event attestation — learner ticks a "I attended this event"
 *     checkbox + optional short statement before credit posts. Often
 *     paired with one of the others for CE-style compliance.
 *
 *   • Admin-marked attendance (STUB / disabled) — admin opens an
 *     attendance panel post-event and ticks attendees. Backend lift;
 *     toggle is rendered but disabled with a "coming soon" label.
 *
 *   • Webinar API integration (STUB / disabled) — pulls attendance
 *     from Zoom / GoToWebinar / Teams. Heavy backend work; placeholder
 *     only so admins see the surface coming.
 *
 */

import { CourseEventVerification } from '../apis/api-client';

export interface EventVerificationConfig {
  /** Confirmation-code method on/off. */
  codeEnabled: boolean;
  /** The code learners must enter. Case-insensitive comparison on
   *  read; we trim and uppercase before compare to keep "abc123"
   *  matching "ABC123" without surprising admins. */
  code: string;
  /** Post-event attestation on/off. */
  attestationEnabled: boolean;
  /** Optional supplemental statement the learner sees above the
   *  attestation checkbox (e.g. "I attended at least 80% of the
   *  session and watched the recorded portions live."). Free text;
   *  empty falls back to a generic default in the UI. */
  attestationText: string;
  /** Admin-marked attendance (stub — disabled in UI today). Persisted
   *  so the toggle state round-trips, but no learner-facing behavior
   *  ties to it yet. */
  adminMarkedEnabled: boolean;
  /** Webinar platform integration (stub — disabled in UI today). */
  webinarApiEnabled: boolean;
}

export const DEFAULT_VERIFICATION: EventVerificationConfig = {
  codeEnabled: false,
  code: '',
  attestationEnabled: false,
  attestationText: '',
  adminMarkedEnabled: false,
  webinarApiEnabled: false,
};

/**
 * Normalizes the loaded course's `eventVerification` (`course?.eventVerification`)
 * into a fully-populated config. A null/undefined value (event never had
 * verification configured) reads as the default config (everything off).
 */
export function getEventVerification(
  raw: CourseEventVerification | undefined | null,
): EventVerificationConfig {
  const found = raw;
  if (!found) return { ...DEFAULT_VERIFICATION };
  return {
    codeEnabled: found.codeEnabled === true,
    code: typeof found.code === 'string' ? found.code : '',
    attestationEnabled: found.attestationEnabled === true,
    attestationText:
      typeof found.attestationText === 'string' ? found.attestationText : '',
    adminMarkedEnabled: found.adminMarkedEnabled === true,
    webinarApiEnabled: found.webinarApiEnabled === true,
  };
}

/**
 * Returns true when the admin has enabled at least one method that
 * the learner can act on themselves (code entry or attestation).
 * Drives whether the learner-facing event page renders the
 * verification panel at all, vs. just a "Register" button.
 */
export function hasLearnerVerification(config: EventVerificationConfig): boolean {
  return config.codeEnabled || config.attestationEnabled;
}

/**
 * Case-insensitive code match. Trims surrounding whitespace on both
 * sides — admins often paste codes with a stray space, and learners
 * routinely type them with caps lock on. Returns true only when the
 * config has a non-empty code AND it matches.
 */
export function codeMatches(
  config: EventVerificationConfig,
  attempt: string,
): boolean {
  if (!config.codeEnabled) return false;
  const expected = config.code.trim().toUpperCase();
  const actual = (attempt ?? '').trim().toUpperCase();
  return expected.length > 0 && expected === actual;
}
